import { prefersReducedMotion } from '@lucid/core';

class LucidReviews extends HTMLElement {
  #frame = 0;
  #resizeObserver = null;
  #revealObserver = null;
  #drag = null;
  #suppressClick = false;

  connectedCallback() {
    this.rail = this.querySelector('[data-reviews-rail]');
    this.prev = this.querySelector('[data-reviews-prev]');
    this.next = this.querySelector('[data-reviews-next]');
    this.bars = Array.from(this.querySelectorAll('[data-reviews-progress]'));

    this.#setupReveal();
    if (!this.rail) return;

    this.rail.addEventListener('scroll', this.#schedule, { passive: true });
    this.rail.addEventListener('pointerdown', this.#onPointerDown);
    this.rail.addEventListener('click', this.#onClickCapture, true);
    this.rail.addEventListener('dragstart', this.#onDragStart);
    this.prev?.addEventListener('click', this.#onPrev);
    this.next?.addEventListener('click', this.#onNext);
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(this.rail);
    this.#sync();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.#schedule);
    this.rail?.removeEventListener('pointerdown', this.#onPointerDown);
    this.rail?.removeEventListener('click', this.#onClickCapture, true);
    this.rail?.removeEventListener('dragstart', this.#onDragStart);
    this.prev?.removeEventListener('click', this.#onPrev);
    this.next?.removeEventListener('click', this.#onNext);
    this.#endDrag();
    this.#resizeObserver?.disconnect();
    this.#revealObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #behavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  get #step() {
    const card = this.rail.firstElementChild;
    if (!card) return this.rail.clientWidth;
    const gap = Number.parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);

  #move(sign) {
    const step = this.#step;
    const perClick = Math.max(1, Math.min(2, Math.floor(this.rail.clientWidth / step)));
    this.rail.scrollBy({ left: sign * step * perClick, behavior: this.#behavior });
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  };

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    const max = scrollWidth - clientWidth;
    const position = Math.abs(scrollLeft);
    if (this.prev) this.prev.disabled = position <= 2;
    if (this.next) this.next.disabled = position >= max - 2;
    const visible = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
    const progress = max > 0 ? visible + (1 - visible) * (position / max) : 1;
    for (const bar of this.bars) bar.style.setProperty('--lc-p', progress.toFixed(3));
  }

  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.#drag = { x: event.clientX, left: this.rail.scrollLeft, moved: false };
    this.#suppressClick = false;
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (!this.#drag) return;
    const delta = event.clientX - this.#drag.x;
    if (!this.#drag.moved && Math.abs(delta) > 4) {
      this.#drag.moved = true;
      this.rail.classList.add('is-dragging');
    }
    if (this.#drag.moved) this.rail.scrollLeft = this.#drag.left - delta;
  };

  #onPointerUp = () => {
    if (!this.#drag) return;
    const moved = this.#drag.moved;
    this.#endDrag();
    if (!moved) return;
    this.#suppressClick = true;
    const step = this.#step;
    const target = Math.round(this.rail.scrollLeft / step) * step;
    this.rail.classList.remove('is-dragging');
    this.rail.scrollTo({ left: target, behavior: this.#behavior });
  };

  #endDrag() {
    this.#drag = null;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
  }

  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #onDragStart = (event) => event.preventDefault();

  #setupReveal() {
    const targets = this.querySelectorAll('[data-reviews-reveal]');
    const active =
      document.documentElement.classList.contains('lucid-reveal') &&
      !window.Shopify?.designMode &&
      !prefersReducedMotion() &&
      'IntersectionObserver' in window;
    if (!active || targets.length === 0) return;

    const pending = Array.from(targets).filter((target) => target.getBoundingClientRect().top > window.innerHeight);
    if (pending.length === 0) return;

    this.setAttribute('data-animate', '');
    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          this.#revealObserver?.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    for (const target of targets) {
      if (pending.includes(target)) {
        this.#revealObserver.observe(target);
      } else {
        target.classList.add('is-in');
      }
    }
  }
}

if (!customElements.get('lucid-reviews')) customElements.define('lucid-reviews', LucidReviews);
